import type { HotelRoomRateMeta } from "@b2bportal/core-types";
import { subtractPrices } from "@b2bportal/core-utilities";
import { MealPlanKind } from "@b2bportal/lodging-api";

/**
 * Filters rates to only include those matching the selected rate's
 * cancellation policy and origin.
 */
export const getMealPlanRatesForBaseRate = (
  rates: HotelRoomRateMeta[],
  selectedRate: HotelRoomRateMeta
): HotelRoomRateMeta[] => {
  const currRefundability =
    selectedRate.product.cancellationPolicyV2.CancellationPolicyV2;
  const currOrigin = selectedRate.origin;

  return rates.filter(
    (r) =>
      r.product.cancellationPolicyV2.CancellationPolicyV2 ===
        currRefundability && r.origin === currOrigin
  );
};

/**
 * Computes price difference for each meal plan rate relative to the provided base rate.
 * - Base rate gets priceDifference: undefined
 * - Other meal plans get priceDifference: (their price - base price)
 */
export const getMealPlanRatesWithRelativePriceDiff = (
  mealPlanRates: HotelRoomRateMeta[],
  baseRate: HotelRoomRateMeta
): HotelRoomRateMeta[] => {
  const baseNightlyPrice = baseRate.product.perNightTotal;

  return mealPlanRates.map((rate): HotelRoomRateMeta => {
    // Base rate has no price difference
    if (rate.key === baseRate.key) {
      return { ...rate, priceDifference: undefined };
    }

    const relativePriceDiff = subtractPrices(
      rate.product.perNightTotal,
      baseNightlyPrice
    );

    return {
      ...rate,
      priceDifference: relativePriceDiff,
    };
  });
};

/**
 * Determines if meal plans section should be shown.
 * Shows if there are multiple rates, or a single rate that's not Room only.
 */
export const shouldShowMealPlans = (
  mealPlanRates: HotelRoomRateMeta[]
): boolean => {
  return (
    mealPlanRates.length > 1 ||
    (mealPlanRates.length === 1 &&
      mealPlanRates[0].product.mealPlan?.kind !== MealPlanKind.RoomOnly)
  );
};
